import { c as _c } from "react/compiler-runtime";
import { dateRangeAtom } from "@/hooks/useConnectedGithubOrgs";
import { CORE_ANALYTICS_EVENTS } from "@/lib/posthog-constants";
import { labelsToDateRanges } from "@/lib/utils";
import { useAtom, useSetAtom } from "jotai";
import { usePostHog } from "posthog-js/react";
import React, { useRef } from "react";
import type { DateRange } from "react-day-picker";
import { MetricsDateRangePicker } from "./date-picker-range";
import { Tabs, TabsList, TabsTrigger } from "@repo/blacksmith-ui/tabs";
import { cn } from "@repo/blacksmith-ui/cn";
import { dateRangeUserTouchedAtom, recentDateRangeAtom, recentTimeTabAtom, tabAtom } from "@/atoms/runsFilterAtoms";
import { useAnimatedIndicator } from "@/hooks/useAnimatedIndicator";
import { useDateRangeQueryParams } from "@/hooks/useDateRangeQueryParams";
export interface DateRangeOption {
  label: string;
  value: string;
}

// Default date range options for most pages
const DEFAULT_DATE_RANGE_OPTIONS: DateRangeOption[] = [{
  label: "MTD",
  value: "MTD"
}, {
  label: "7D",
  value: "7D"
}, {
  label: "30D",
  value: "30D"
}, {
  label: "3M",
  value: "3M"
}, {
  label: "6M",
  value: "6M"
}, {
  label: "12M",
  value: "12M"
}];

// Re-export for backward compatibility
export { tabAtom };
export default function DateRangeTabs(t0) {
  const $ = _c(41);
  const {
    onMouseOver,
    dateRangeOptions: t1,
    source,
    enableTimeSelection,
    containerClassName,
    tabSelection,
    defaultTab
  } = t0;
  const dateRangeOptions = t1 === undefined ? DEFAULT_DATE_RANGE_OPTIONS : t1;
  const isSourceRecent = source === "recent_range";
  const [, setDateRange] = useAtom(isSourceRecent ? recentDateRangeAtom : dateRangeAtom);
  const [globalTab, setGlobalTab] = useAtom(isSourceRecent ? recentTimeTabAtom : tabAtom);
  const tab = tabSelection?.value ?? globalTab;
  const setTab = tabSelection?.onValueChange ?? setGlobalTab;
  const markDateRangeTouched = useSetAtom(dateRangeUserTouchedAtom);
  const labelToDateRange = labelsToDateRanges();
  const posthog = usePostHog();
  const t2 = !tabSelection;
  let t3;
  if ($[0] !== defaultTab || $[1] !== source || $[2] !== t2) {
    t3 = {
      source,
      hydrate: t2,
      defaultTab
    };
    $[0] = defaultTab;
    $[1] = source;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    updateDateRangeUrl
  } = useDateRangeQueryParams(t3);
  const listRef = useRef(null);
  const triggerRefs = useRef(new Map());
  const customDatePickerRef = useRef(null);
  const indicator = useAnimatedIndicator(listRef, {
    getActiveElement: () => {
      if (tab === "custom") {
        return customDatePickerRef.current;
      }
      return triggerRefs.current.get(tab) ?? null;
    },
    deps: [tab, dateRangeOptions]
  });
  let t4;
  if ($[4] !== posthog) {
    t4 = range => {
      posthog?.capture(CORE_ANALYTICS_EVENTS.DATE_RANGE_CHANGE, {
        date_range: range
      });
    };
    $[4] = posthog;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleDateRangeChange = t4;
  let t5;
  if ($[6] !== markDateRangeTouched || $[7] !== posthog || $[8] !== setDateRange || $[9] !== setTab || $[10] !== source || $[11] !== updateDateRangeUrl) {
    t5 = dateRange => {
      markDateRangeTouched(true);
      setTab("custom");
      setDateRange(dateRange);
      updateDateRangeUrl("custom", dateRange);
      posthog?.capture(CORE_ANALYTICS_EVENTS.DATE_RANGE_CUSTOM_SELECT, {
        ...(source && {
          source
        })
      });
    };
    $[6] = markDateRangeTouched;
    $[7] = posthog;
    $[8] = setDateRange;
    $[9] = setTab;
    $[10] = source;
    $[11] = updateDateRangeUrl;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleCustomDateSelect = t5;
  const T0 = Tabs;
  const t6 = value => {
    markDateRangeTouched(true);
    setDateRange({
      ...labelToDateRange[value]
    });
    setTab(value);
    handleDateRangeChange(value);
    updateDateRangeUrl(value);
  };
  const T1 = TabsList;
  let t7;
  if ($[13] !== containerClassName) {
    t7 = cn("relative h-auto flex-nowrap bg-[#eeeeee] dark:bg-[#2A2C2C] rounded-lg p-0.5 gap-0", containerClassName);
    $[13] = containerClassName;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = !indicator.ready && "opacity-0";
  let t9;
  if ($[15] !== t8) {
    t9 = cn("absolute left-0 top-0.5 bottom-0.5 rounded-md border", "bg-white dark:bg-[#1D1E20] border-[#C5C5C5] dark:border-[#7C7D7F]", "transition-[transform,width] duration-150 ease-out will-change-[transform,width]", t8);
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = `${indicator.width}px`;
  const t11 = `translateX(${indicator.left}px)`;
  let t12;
  if ($[17] !== t10 || $[18] !== t11) {
    t12 = {
      width: t10,
      transform: t11
    };
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t12 || $[21] !== t9) {
    t13 = <div aria-hidden={true} className={t9} style={t12} />;
    $[20] = t12;
    $[21] = t9;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const t15 = dateRangeOptions.map(t14 => {
    const {
      label,
      value: value_0
    } = t14;
    return <TabsTrigger key={value_0} value={value_0} onMouseOver={() => {
      onMouseOver?.(labelToDateRange[value_0]);
    }} className={cn("relative z-10 h-[22px] px-2 text-xs leading-4 transition-colors rounded-md min-w-0", "border border-transparent cursor-pointer", "focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0", "data-[state=active]:bg-transparent dark:data-[state=active]:bg-transparent", "data-[state=active]:border-transparent", "data-[state=active]:text-[#161718] dark:data-[state=active]:text-white", "data-[state=active]:font-medium", "data-[state=inactive]:bg-transparent", "data-[state=inactive]:text-neutral-900 dark:data-[state=inactive]:text-neutral-200/80", "data-[state=inactive]:font-normal", "hover:text-neutral-600 dark:hover:text-neutral-300", "data-[state=active]:hover:bg-transparent")} ref={el => {
      triggerRefs.current.set(value_0, el);
    }}>{label}</TabsTrigger>;
  });
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="relative z-10 h-[22px] mx-1 w-px bg-neutral-300 dark:bg-neutral-600" />;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const t17 = tab === "custom";
  let t18;
  if ($[24] !== customDatePickerRef || $[25] !== enableTimeSelection || $[26] !== handleCustomDateSelect || $[27] !== source || $[28] !== t17) {
    t18 = <MetricsDateRangePicker source={source} onSelect={handleCustomDateSelect} enableTimeSelection={enableTimeSelection} buttonRef={customDatePickerRef} isActive={t17} />;
    $[24] = customDatePickerRef;
    $[25] = enableTimeSelection;
    $[26] = handleCustomDateSelect;
    $[27] = source;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== T1 || $[31] !== t13 || $[32] !== t15 || $[33] !== t18 || $[34] !== t7) {
    t19 = <T1 ref={listRef} className={t7}>{t13}{t15}{t16}{t18}</T1>;
    $[30] = T1;
    $[31] = t13;
    $[32] = t15;
    $[33] = t18;
    $[34] = t7;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== T0 || $[37] !== t19 || $[38] !== t6 || $[39] !== tab) {
    t20 = <T0 value={tab} onValueChange={t6}>{t19}</T0>;
    $[36] = T0;
    $[37] = t19;
    $[38] = t6;
    $[39] = tab;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  return t20;
}